/**
 * P3 / P0.1.12 — Legal acceptance guard.
 *
 * Wraps the authenticated app. On mount (and whenever the http client
 * fires the `cm:pending-legal-acceptance` event from a backend 451), this
 * guard queries `/legal/required-acceptances`. If the list is non-empty,
 * a full-screen modal blocks the app until the user accepts every
 * pending doc.
 *
 * Accept = required checkbox per doc + a single primary CTA. Each click
 * POSTs `/legal/accept` with {doc, locale, sha256}. After all required
 * docs are accepted, the modal closes and the protected app renders.
 *
 * Design notes:
 *  - Modal cannot be dismissed by Esc / outside-click (blocking gate).
 *  - "Decline" CTA logs the user out (only way out without accepting).
 *  - Doc body fetched per locale from `/legal/{doc}/{locale}`. Markdown
 *    rendered as plain text (whitelisted minimal styling) — we do NOT
 *    pull in a markdown engine here; keep bundle slim. Counsel can write
 *    in a constrained style + the doc gets rendered as readable
 *    pre-formatted text.
 */

import { useEffect, useState, useCallback, ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Stack,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Link as MuiLink,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import httpClient from '../../utils/httpClient';
import { useAuth } from '../../contexts/AuthContext';
import { colors, designTokens } from '../../theme/theme';

interface PendingDoc {
  doc: string;
  current_sha256: string;
  title: string;
  last_accepted_sha256: string | null;
  last_accepted_at: string | null;
}

interface PendingResponse {
  pending: PendingDoc[];
}

const TEXT_PRIMARY = colors.neutral[900];
const TEXT_SECONDARY = colors.neutral[600];
const ACCENT_GRADIENT = colors.primary.gradient;
const ACCENT_DEEP = colors.primary.dark;

export default function LegalAcceptanceGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, logout } = useAuth();
  const { i18n, t } = useTranslation();
  const locale = (i18n.resolvedLanguage || 'en') as 'en' | 'az' | 'ru';

  const [pending, setPending] = useState<PendingDoc[] | null>(null);
  const [bodyByDoc, setBodyByDoc] = useState<Record<string, string>>({});
  const [checkedByDoc, setCheckedByDoc] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshPending = useCallback(async () => {
    if (!isAuthenticated) {
      setPending(null);
      return;
    }
    try {
      const res = await httpClient.get<PendingResponse>('/legal/required-acceptances');
      setPending(res.data.pending);
      // Reset checkbox state for any newly-pending docs
      setCheckedByDoc((prev) => {
        const next = { ...prev };
        for (const p of res.data.pending) {
          if (!(p.doc in next)) next[p.doc] = false;
        }
        return next;
      });
    } catch (e: any) {
      // Most likely no token yet (race during login). Silent — auth state
      // change re-fires this effect.
    }
  }, [isAuthenticated]);

  // Initial + auth-state-change check
  useEffect(() => {
    refreshPending();
  }, [refreshPending]);

  // Backend 451 from any endpoint also re-opens the modal
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as PendingResponse | undefined;
      if (detail?.pending?.length) {
        setPending(detail.pending);
      } else {
        refreshPending();
      }
    };
    window.addEventListener('cm:pending-legal-acceptance', handler);
    return () => window.removeEventListener('cm:pending-legal-acceptance', handler);
  }, [refreshPending]);

  // Fetch doc bodies whenever pending list or locale changes
  useEffect(() => {
    if (!pending?.length) return;
    let cancelled = false;
    (async () => {
      const bodies: Record<string, string> = {};
      for (const p of pending) {
        try {
          const res = await httpClient.get<string>(`/legal/${p.doc}/${locale}`, {
            responseType: 'text',
          });
          bodies[p.doc] = res.data as unknown as string;
        } catch {
          bodies[p.doc] = '[Failed to load document — please refresh.]';
        }
      }
      if (!cancelled) setBodyByDoc(bodies);
    })();
    return () => {
      cancelled = true;
    };
  }, [pending, locale]);

  const allChecked = pending?.every((p) => checkedByDoc[p.doc]) ?? false;
  const open = isAuthenticated && pending !== null && pending.length > 0;

  const handleAcceptAll = async () => {
    if (!pending) return;
    setSubmitting(true);
    setError(null);
    try {
      for (const p of pending) {
        await httpClient.post('/legal/accept', {
          doc: p.doc,
          locale,
          sha256: p.current_sha256,
        });
      }
      // Verify the gate has cleared
      await refreshPending();
      setPending([]);
    } catch (e: any) {
      setError(e?.message || 'Failed to record acceptance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = () => {
    // No way through the gate without accepting — log them out
    if (window.confirm(t('cookies.title') /* fallback string; see footer-link below */)) {
      logout();
    } else {
      logout();
    }
  };

  return (
    <>
      {!open && children}
      <Dialog
        open={open}
        fullWidth
        maxWidth="md"
        disableEscapeKeyDown
        // Prevent backdrop click from dismissing — gate is mandatory
        onClose={(_e, reason) => {
          if (reason === 'backdropClick') return;
        }}
        PaperProps={{
          sx: {
            background: designTokens.glass.surface,
            backdropFilter: designTokens.glass.blur,
            border: `1px solid ${designTokens.glass.border}`,
            borderRadius: `${designTokens.radius.lg}px`,
          },
        }}
      >
        <DialogTitle sx={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
          {t('legal.gate.title', 'Please review and accept the latest terms')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: TEXT_SECONDARY, mb: 2 }}>
            {t(
              'legal.gate.intro',
              'We need you to accept the latest Terms & Conditions and Privacy Policy before continuing. You can read each in full below.',
            )}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2}>
            {(pending ?? []).map((p) => (
              <Box
                key={p.doc}
                sx={{
                  border: `1px solid ${designTokens.glass.border}`,
                  borderRadius: `${designTokens.radius.md}px`,
                  p: 2,
                  background: 'rgba(255,255,255,0.02)',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" sx={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
                    {p.title}
                  </Typography>
                  {p.last_accepted_sha256 && (
                    <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
                      {t('legal.gate.updated_since_last', 'Updated since you last accepted')}
                    </Typography>
                  )}
                </Stack>

                <Box
                  sx={{
                    maxHeight: 240,
                    overflowY: 'auto',
                    p: 2,
                    background: 'rgba(0,0,0,0.25)',
                    borderRadius: `${designTokens.radius.sm}px`,
                    fontSize: 13,
                    lineHeight: 1.55,
                    color: TEXT_SECONDARY,
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                  }}
                >
                  {bodyByDoc[p.doc] ?? '…'}
                </Box>

                <FormControlLabel
                  sx={{ mt: 1, color: TEXT_PRIMARY }}
                  control={
                    <Checkbox
                      checked={checkedByDoc[p.doc] ?? false}
                      onChange={(_, v) =>
                        setCheckedByDoc((s) => ({ ...s, [p.doc]: v }))
                      }
                      sx={{ color: TEXT_SECONDARY, '&.Mui-checked': { color: colors.primary.main } }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: TEXT_PRIMARY }}>
                      {t('legal.gate.checkbox', 'I have read and accept the {{title}}', { title: p.title })}{' '}
                      <MuiLink
                        href={`/legal/${p.doc}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: colors.primary.main }}
                      >
                        {t('legal.gate.open_full', 'open full page')}
                      </MuiLink>
                    </Typography>
                  }
                />
              </Box>
            ))}
          </Stack>
        </DialogContent>

        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={1.25}
          sx={{ p: 3, pt: 2, justifyContent: 'space-between' }}
        >
          <Button
            onClick={handleDecline}
            disabled={submitting}
            sx={{ color: TEXT_SECONDARY }}
          >
            {t('legal.gate.decline_logout', 'Decline and log out')}
          </Button>
          <Button
            onClick={handleAcceptAll}
            disabled={!allChecked || submitting}
            variant="contained"
            sx={{
              background: ACCENT_GRADIENT,
              color: '#fff',
              fontWeight: 600,
              minWidth: 180,
              '&:hover': { background: ACCENT_DEEP },
              '&.Mui-disabled': { opacity: 0.5, color: 'rgba(255,255,255,0.6)' },
            }}
          >
            {submitting ? (
              <CircularProgress size={18} sx={{ color: '#fff' }} />
            ) : (
              t('legal.gate.accept_all', 'Accept and continue')
            )}
          </Button>
        </Stack>
      </Dialog>
    </>
  );
}
